import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import Status from 'common/components/Status/Status';
import Button from 'common/components/Button/Button';
import truncateString from 'common/utils/truncateString';
import ImageFromStrapiMedia from 'common/components/ImageFromStrapiMedia/ImageFromStrapiMedia';
import { Content } from 'types/Content';
import ImageComponent from 'common/components/ImageComponent/ImageComponent';
import Icon from 'components/Icon/Icon';
import getFlattenText from 'common/utils/getFlattenText';
import useAuth from 'common/hooks/useAuth';

const containerStyle =
  'white-box flex px-4 py-4 mb-2 w-full relative cursor-pointer transition-all overflow-hidden';

function ContentItem({ item, onDelete, onNavigate }: any) {
  const {
    id,
    title,
    authorOperator,
    author,
    description,
    images,
    status,
    category,
    updatedAt,
    documents,
  } = item as Content;

  const { t } = useTranslation();
  const { user } = useAuth();

  const detailLink = `/contents/${id}`;

  return (
    <div className={`${containerStyle}  items-start lg:items-stretch flex-col lg:flex-row`}>
      <Link to={detailLink} className="w-full">
        <div className="responsive-flex-lg mb-4 lg:mb-0 w-full gap-2 lg:gap-4 pr:0 lg:pr-4">
          <div className="flex flex-col flex-1 justify-start overflow-hidden">
            {/* head */}
            <div className="flex flex-row w-full gap-4 flex-1 justify-between">
              <div className="flex flex-col flex-1">
                <h4 className="text-xl font-bold text-primary leading-6">{title}</h4>
              </div>
              <h4 className="text-md text-slate-500 leading-6 flex flex-row justify-start items-center font-normal text-sm">
                <Icon name="ClockIcon" className="w-4 h-4 mr-1" />
                {dayjs(updatedAt).fromNow()}
              </h4>
            </div>

            <div className="responsive-flex-lg flex-row justify-between mt-1">
              <h4 className="flex-1 text-md text-slate-500 leading-6 flex flex-row justify-start items-center font-normal text-sm">
                <div className="border bg-stone-200 border-stone-200 text-stone-800 rounded-full py-0 px-2">
                  {category?.name}
                </div>
              </h4>
              <div className="flex flex-row ">
                <h4 className="font-bold text-md text-slate-500 leading-6 flex flex-row items-center">
                  <Icon name="UserIcon" className="w-4 h-4 mr-1" />
                  {authorOperator?.username ?? author?.username ?? 'Auteur inconnu'}
                </h4>
              </div>
            </div>

            <div className="responsive-flex-lg mt-1" />

            {/* foot */}
            <div className="flex flex-row gap-2 pt-3 border-t border-slate-200">
              <div className="flex flex-row gap-4 relative shrink-0">
                <div className="flex flex-col items-center">
                  {images
                    ? images?.map((image: any) => (
                        <div key={image.id} className="w-24 h-24 rounded-lg overflow-hidden">
                          <ImageComponent image={ImageFromStrapiMedia(image)?.uri} />
                        </div>
                      ))
                    : null}
                  {documents?.length > 0 ? (
                    <div className="flex flex-row items-center justify-center mt-1 font-bold ">
                      {documents.length} <Icon name="AttachedFile" className="w-4 h-4" />
                    </div>
                  ) : null}
                </div>
              </div>
              {description && (
                <div className="text-sm text-gray-500 pt-2 flex-1 h-full pl-3 ml-2 border-l border-slate-200">
                  {truncateString(getFlattenText(description), 150)}
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* right */}
      <div className="flex flex-col justify-between text-sm w-full lg:border-l lg:pl-4 lg:w-[30em] lg:border-slate-150 gap-2">
        <div className="flex w-full justify-between text-slate-600">
          {status ? <Status status={status} /> : <div />}
        </div>

        {user?.isSuperAdmin && (
          <div className="flex w-full gap-1 justify-end">
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item?.id)}
              confirmMessage={t('actions.confirmDelete')}
            />

            {user?.isJuloaAdmin && (
              <Button
                title={t('actions.edit')}
                type="warning-line"
                icon="PencilSquareIcon"
                onClick={() => onNavigate(item?.id)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContentItem;
